import * as THREE from 'three';
import { BRASS, DSTEEL, DWOOD, GOLD, LEATHER_BELT, STEEL, WOOD, cached, emis, leather, metal, std, stone } from '../materials.ts';
import { G, M, V, flame, glowSprite, heaterShield, limb, ring, sword, type Flame } from '../prims.ts';
import { LIGHT_K, begin, done, pointLight, type BaseOpts, type Model } from './types.ts';

/** Rune colors per rarity: common, uncommon, rare, very rare, legendary. */
export const RARITY = ['#ffffff', '#47d16a', '#3a8bff', '#b05cff', '#ff9a2a'] as const;

export const swords = (o?: BaseOpts): Model => {
  const c = begin(o, 0.75, 80);
  const { g, rng } = c;
  const items: THREE.Group[] = [];
  const flames: Flame[] = [];
  for (let t = 0; t < 5; t++) {
    const s = G(g, (t - 2) * 0.27, 0.05, 0);
    const blade = t === 4 ? metal('#e6d6b0', { roughness: 0.2 }) : t === 3 ? metal('#c9cfe0', { roughness: 0.2 }) : STEEL;
    const sw = sword(0.78, blade, t === 0 ? DWOOD : t >= 3 ? GOLD : BRASS);
    s.add(sw);
    const col = RARITY[t]!;
    if (t > 0) {
      M(new THREE.OctahedronGeometry(0.03), emis(col, col, 1.2), sw).position.y = -0.03;
      glowSprite(sw, col, 0.22, V(0, -0.03, 0.02), 0.7);
      const rn = emis(col, col, 0.9);
      for (let i = 0; i < t; i++) M(new THREE.BoxGeometry(0.012, 0.04, 0.014), rn, sw).position.set(0, 0.3 + i * 0.1, 0);
    }
    if (t === 4) for (let i = 0; i < 3; i++) flames.push(flame(sw, V(0, 0.4 + i * 0.22, 0), 0.35, rng));
    items.push(s);
  }
  const tk = (n: number): void => {
    for (const f of flames) f.flick();
    items.forEach((s, i) => { s.rotation.y = Math.sin(n * 0.2 + i) * 0.6; });
  };
  tk(0);
  return done(c, 1.05, tk);
};

export const shields = (o?: BaseOpts): Model => {
  const c = begin(o, 0.75, 81);
  const { g } = c;
  const s1 = G(g, -0.48, 0.32, 0);
  M(new THREE.CylinderGeometry(0.22, 0.22, 0.04, 14), WOOD, s1).rotation.x = Math.PI / 2;
  M(new THREE.TorusGeometry(0.22, 0.018, 5, 20), DSTEEL, s1);
  M(new THREE.SphereGeometry(0.05, 8, 6), DSTEEL, s1).position.z = 0.03;
  const s2 = G(g, 0, 0.4, 0);
  const hs = heaterShield(0.36, 0.5, metal('#1f3a6e', { roughness: 0.45 }));
  s2.add(hs);
  hs.position.z = -0.02;
  M(new THREE.TorusGeometry(0.06, 0.014, 5, 14), GOLD, s2).position.set(0, 0.05, 0.05);
  const s3 = G(g, 0.5, 0.45, 0);
  s3.add(heaterShield(0.38, 0.64, metal('#dfe6ee', { roughness: 0.2 })));
  const rn = emis('#ffe3a0', '#ffb020', 1);
  for (let i = 0; i < 5; i++) M(new THREE.BoxGeometry(0.02, 0.06, 0.01), rn, s3).position.set(-0.08 + i * 0.04, 0.05, 0.06);
  glowSprite(s3, '#ffc65a', 0.5, V(0, 0.05, 0.08), 0.35);
  const t = (n: number): void => {
    [s1, s2, s3].forEach((s, i) => { s.rotation.y = Math.sin(n * 0.2 + i) * 0.7; });
  };
  t(0);
  return done(c, 0.8, t);
};

/* ---------- relics ---------- */

export type UnsleepingRelicId = 'corvinGate' | 'velaString' | 'anselPage' | 'lucanCenser' | 'bruniHammer' | 'ragnaHorn' | 'eddaShield' | 'kaelaSeed';
export type RelicId = 'fateDie' | 'vigilGrimoire' | 'vigilLantern' | 'dreamerRing' | 'warHorn' | 'ashAmulet' | 'brazier' | UnsleepingRelicId;
/** Each Unsleeping's light (his relic glows in it, like his trophy). */
export const UNSLEEPING_RELIC_COLOR: Record<UnsleepingRelicId, string> = {
  corvinGate: '#ff4f3a',
  velaString: '#7fa8ff',
  anselPage: '#ff9a3a',
  lucanCenser: '#b07aff',
  bruniHammer: '#4fe0ff',
  ragnaHorn: '#ff3a6a',
  eddaShield: '#dfe8ff',
  kaelaSeed: '#5fae3a',
};
export interface RelicOpts extends BaseOpts { id: RelicId }

const isUnsleepingRelic = (o: RelicOpts): o is BaseOpts & { id: UnsleepingRelicId } => o.id in UNSLEEPING_RELIC_COLOR;

export const relic = (o: RelicOpts): Model => {
  if (isUnsleepingRelic(o)) return unsleepingRelic(o);
  const c = begin(o, 0.55, 90 + o.id.length);
  const { g, R, rng } = c;
  switch (o.id) {
    case 'fateDie': {
      const die = G(g, 0, 0.5, 0);
      const geo = new THREE.IcosahedronGeometry(0.26, 0);
      M(geo, std('#e6dcc6', { roughness: 0.5, flatShading: true }), die);
      die.add(new THREE.LineSegments(new THREE.EdgesGeometry(geo), cached('line#c9a24a', () => new THREE.LineBasicMaterial({ color: '#c9a24a' }))));
      glowSprite(g, '#c9a24a', 0.9, V(0, 0.5, 0), 0.25);
      return done(c, 0.8, (n) => {
        die.rotation.x = n * 0.18;
        die.rotation.z = n * 0.11;
        die.position.y = 0.5 + (n % 12 < 6 ? 0.03 : 0);
      });
    }
    case 'vigilGrimoire': {
      const b = G(g, 0, 0.42, 0);
      b.rotation.set(-0.5, 0, 0.1);
      const cov = leather('#4a1c1c');
      M(new THREE.BoxGeometry(0.42, 0.05, 0.56), cov, b).position.y = -0.04;
      M(new THREE.BoxGeometry(0.42, 0.05, 0.56), cov, b).position.y = 0.08;
      M(new THREE.BoxGeometry(0.4, 0.08, 0.53), std('#e3d6b6', { roughness: 1 }), b).position.set(0.01, 0.02, 0);
      M(new THREE.BoxGeometry(0.06, 0.18, 0.56), cov, b).position.set(-0.21, 0.02, 0);
      for (const z of [-0.2, 0.2]) M(new THREE.BoxGeometry(0.08, 0.16, 0.04), GOLD, b).position.set(0.21, 0.02, z);
      ring(b, 0.09, 0.012, emis('#ffd88a', '#ffb020', 1), 0.11, 5, 20);
      const runes: THREE.Sprite[] = [];
      for (let i = 0; i < 5; i++) runes.push(glowSprite(g, '#ffc65a', 0.08, V(0, 0.6, 0), 0.9, true));
      const t = (n: number): void => {
        b.position.y = 0.42 + (n % 12 < 6 ? 0.03 : 0);
        runes.forEach((r, i) => {
          const p = (i / 5 + n * 0.04) % 1, a = i * 1.3 + p * 3;
          r.position.set(Math.cos(a) * 0.2, 0.55 + p * 0.5, Math.sin(a) * 0.2);
          r.material.opacity = 0.9 * (1 - p);
        });
      };
      t(0);
      return done(c, 1.0, t);
    }
    case 'vigilLantern': {
      M(new THREE.CylinderGeometry(0.16, 0.18, 0.05, 8), DSTEEL, g).position.y = 0.03;
      M(new THREE.ConeGeometry(0.18, 0.16, 8), DSTEEL, g).position.y = 0.6;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        limb(g, V(Math.cos(a) * 0.15, 0.05, Math.sin(a) * 0.15), V(Math.cos(a) * 0.15, 0.52, Math.sin(a) * 0.15), 0.01, 0.01, DSTEEL, 4);
      }
      M(new THREE.TorusGeometry(0.06, 0.012, 5, 12), DSTEEL, g).position.y = 0.74;
      const glass = cached('lanternGlass', () => new THREE.MeshStandardMaterial({ color: '#ffe9b0', transparent: true, opacity: 0.18, side: THREE.DoubleSide, depthWrite: false }));
      M(new THREE.CylinderGeometry(0.14, 0.14, 0.46, 8, 1, true), glass, g).position.y = 0.29;
      const fl = flame(g, V(0, 0.15, 0), 0.9, rng, '#7fd6ff');
      const L = pointLight(c, 0x9fdcff, 0.9, 3, V(0, 0.3, 0));
      return done(c, 0.8, () => {
        fl.flick();
        L.intensity = (0.7 + rng() * 0.4) * LIGHT_K;
      });
    }
    case 'dreamerRing': {
      const r = G(g, 0, 0.45, 0);
      M(new THREE.TorusGeometry(0.18, 0.035, 8, 24), GOLD, r);
      M(new THREE.CylinderGeometry(0.05, 0.04, 0.05, 8), GOLD, r).position.y = 0.2;
      const gem = M(new THREE.OctahedronGeometry(0.07), emis('#d6a8ff', '#9b4dff', 1, { flatShading: true, transparent: true, opacity: 0.9 }), r);
      gem.position.y = 0.27;
      gem.scale.y = 1.3;
      glowSprite(r, '#9b4dff', 0.5, V(0, 0.27, 0), 0.5);
      return done(c, 0.8, (n) => {
        r.rotation.y = n * 0.2;
        r.rotation.x = Math.sin(n * 0.2) * 0.3;
      });
    }
    case 'warHorn': {
      const h = G(g, 0, 0.32, 0);
      const iv = std('#e6dcc6', { roughness: 0.5 });
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 10; i++) {
        const t = i / 10;
        pts.push(V(-0.32 + t * 0.6, Math.sin(t * Math.PI) * 0.18 + t * 0.12, 0));
      }
      for (let i = 0; i < 10; i++) limb(h, pts[i]!, pts[i + 1]!, 0.025 + i * 0.012, 0.025 + (i + 1) * 0.012, iv, 10);
      for (const i of [2, 5, 8]) {
        const b = M(new THREE.TorusGeometry(0.03 + i * 0.012, 0.012, 5, 14), GOLD, h);
        b.position.copy(pts[i]!);
        b.quaternion.setFromUnitVectors(V(0, 0, 1), new THREE.Vector3().subVectors(pts[i + 1]!, pts[i]!).normalize());
      }
      limb(h, pts[1]!, pts[9]!, 0.004, 0.004, LEATHER_BELT, 3);
      return done(c, 0.75);
    }
    case 'ashAmulet': {
      // NEW: ash-grey stone amulet on an iron chain, with an ember core.
      const am = G(g, 0, 0.36, 0);
      am.rotation.x = 0.15;
      const ash = stone('#6e6b68');
      const disc = M(new THREE.CylinderGeometry(0.16, 0.16, 0.05, 12), ash, am);
      disc.rotation.x = Math.PI / 2;
      M(new THREE.TorusGeometry(0.16, 0.018, 5, 16), stone('#4b4846'), am);
      M(new THREE.TorusGeometry(0.1, 0.008, 4, 14), stone('#8a8784'), am).position.z = 0.026;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const notch = M(new THREE.BoxGeometry(0.012, 0.03, 0.01), stone('#4b4846'), am);
        notch.position.set(Math.cos(a) * 0.13, Math.sin(a) * 0.13, 0.027);
        notch.rotation.z = a - Math.PI / 2;
      }
      const core = M(new THREE.IcosahedronGeometry(0.045, 0), emis('#ffb070', '#ff4a10', 1.4, { flatShading: true }), am);
      core.position.z = 0.03;
      const glowS = glowSprite(am, '#ff6a1a', 0.32, V(0, 0, 0.05), 0.7, true);
      M(new THREE.TorusGeometry(0.025, 0.008, 4, 8), DSTEEL, am).position.y = 0.18;
      // Chain loop above.
      const linkM = DSTEEL;
      const N = 18;
      for (let i = 0; i < N; i++) {
        const a = (i / (N - 1)) * Math.PI * 1.7 - Math.PI * 0.85;
        const x = Math.sin(a) * 0.2, y = 0.2 + (1 - Math.cos(a)) * 0.26;
        const l = M(new THREE.TorusGeometry(0.018, 0.005, 3, 6), linkM, am);
        l.position.set(x, y, 0);
        l.rotation.set(0, i % 2 ? Math.PI / 2 : 0, a);
      }
      const t = (n: number): void => {
        am.rotation.y = Math.sin(n * 0.25) * 0.5;
        glowS.material.opacity = 0.5 + (n % 6) * 0.06;
        core.scale.setScalar(1 + (n % 4 < 2 ? 0.08 : 0));
      };
      t(0);
      return done(c, 1.0, t);
    }
    case 'brazier': {
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        limb(g, V(Math.cos(a) * 0.3, 0, Math.sin(a) * 0.3), V(Math.cos(a) * 0.14, 0.45, Math.sin(a) * 0.14), 0.018, 0.022, DSTEEL, 5);
      }
      M(new THREE.SphereGeometry(0.24, 12, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), metal('#3a3a40', { side: THREE.DoubleSide }), g).position.y = 0.6;
      ring(g, 0.24, 0.02, GOLD, 0.6, 5, 20);
      const coals = emis('#ff6a1a', '#ff3a00', 1.2);
      for (let i = 0; i < 7; i++) M(new THREE.DodecahedronGeometry(0.05), coals, g).position.set(R() * 0.25, 0.58, R() * 0.25);
      const fls = [flame(g, V(0, 0.6, 0), 1.3, rng), flame(g, V(0.08, 0.6, 0.05), 0.9, rng), flame(g, V(-0.07, 0.6, -0.04), 1, rng)];
      const L = pointLight(c, 0xff8a2a, 1.2, 3, V(0, 0.9, 0));
      return done(c, 1.05, () => {
        for (const f of fls) f.flick();
        L.intensity = (1 + rng() * 0.4) * LIGHT_K;
      });
    }
    default:
      throw new Error(`relic ${o.id}`);
  }
};

/**
 * The Unsleeping's relics (docs/ANTIHEROES.md): each a piece of his kit in black glass, cracked with his own light,
 * like his trophy by the keep.
 */
const unsleepingRelic = (o: BaseOpts & { id: UnsleepingRelicId }): Model => {
  const c = begin(o, 0.55, 140 + o.id.length);
  const { g, rng } = c;
  const col = UNSLEEPING_RELIC_COLOR[o.id];
  const glass = cached('unRelicGlass', () => metal('#1b1d24', { roughness: 0.18, metalness: 0.55 }));
  const iron = cached('unRelicIron', () => metal('#3a3d44', { roughness: 0.55, metalness: 0.6 }));
  const lit = cached(`unRelicLit${col}`, () => emis(col, col, 1.3));
  const pale = cached(`unRelicPale${col}`, () => emis('#f2f6ff', col, 1.2));
  /** A crack of light across a face. */
  const crack = (parent: THREE.Object3D, x: number, y: number, z: number, len: number, rot: number, thick = 0.014): THREE.Mesh => {
    const m = M(new THREE.BoxGeometry(len, thick, 0.012), lit, parent);
    m.position.set(x, y, z);
    m.rotation.z = rot;
    return m;
  };
  switch (o.id) {
    case 'corvinGate': {
      // A slab of the gate: four charred planks of black glass, their tops broken, two iron bands, ember in the seams.
      const sl = G(g, 0, 0.06, 0);
      sl.rotation.set(-0.12, 0.35, 0.08);
      const tops = [0.7, 0.8, 0.66, 0.74];
      tops.forEach((h, i) => {
        const pl = M(new THREE.BoxGeometry(0.12, h, 0.05), glass, sl);
        pl.position.set(-0.195 + i * 0.13, h / 2, 0);
        const tip = M(new THREE.ConeGeometry(0.05, 0.08, 3), glass, sl);
        tip.position.set(-0.195 + i * 0.13 + (i % 2 ? 0.02 : -0.02), h + 0.03, 0);
      });
      for (const y of [0.16, 0.54]) M(new THREE.BoxGeometry(0.56, 0.05, 0.035), iron, sl).position.set(0, y, 0.035);
      for (const y of [0.16, 0.54]) for (const x of [-0.2, 0.2]) M(new THREE.SphereGeometry(0.018, 5, 4), iron, sl).position.set(x, y, 0.055);
      // The seams glow through: seen from either side.
      for (let i = 0; i < 3; i++) M(new THREE.BoxGeometry(0.012, 0.6, 0.056), lit, sl).position.set(-0.13 + i * 0.13, 0.35, 0);
      crack(sl, 0.06, 0.38, 0.03, 0.22, 0.7);
      crack(sl, -0.1, 0.26, 0.03, 0.16, -0.5);
      const gl = glowSprite(g, col, 0.95, V(0, 0.4, 0.08), 0.35, true);
      return done(c, 0.9, (n) => {
        gl.material.opacity = 0.28 + (n % 8) * 0.012;
      });
    }
    case 'velaString': {
      // A broken bow of black glass, its string drawn taut in cold light, a wisp of smoke where an arrow would sit.
      const b = G(g, 0, 0.48, 0);
      b.rotation.set(0, 0.5, -0.12);
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 10; i++) {
        const t = i / 10 - 0.5;
        pts.push(V(-0.16 + Math.abs(t) * Math.abs(t) * 0.9, t * 0.8, 0));
      }
      for (let i = 0; i < 10; i++) {
        if (i === 6) continue;
        limb(b, pts[i]!, pts[i + 1]!, 0.022 - Math.abs(i - 5) * 0.002, 0.022 - Math.abs(i + 1 - 5) * 0.002, glass, 6);
      }
      // The snap in the limb, lit from inside.
      M(new THREE.SphereGeometry(0.026, 6, 5), lit, b).position.copy(pts[6]!).lerp(pts[7]!, 0.5);
      M(new THREE.BoxGeometry(0.06, 0.05, 0.05), iron, b).position.copy(pts[5]!);
      const str = limb(b, pts[0]!, pts[10]!, 0.006, 0.006, pale, 4);
      glowSprite(b, col, 0.5, V(0.08, 0, 0), 0.4);
      const wisps: THREE.Sprite[] = [];
      for (let i = 0; i < 4; i++) wisps.push(glowSprite(g, '#9aa4b8', 0.16, V(0, 0.5, 0), 0.5, true));
      const t = (n: number): void => {
        str.scale.x = 1 + Math.sin(n * 1.7) * 0.25;
        wisps.forEach((w, i) => {
          const p = (i / 4 + n * 0.03) % 1;
          w.position.set(-0.02 + p * 0.25, 0.5 + p * 0.28, Math.sin(p * 6 + i) * 0.05);
          w.material.opacity = 0.45 * (1 - p);
        });
      };
      t(0);
      return done(c, 0.95, t);
    }
    case 'anselPage': {
      // A torn page of black glass, its letters burning orange, a small fire licking at one corner.
      // Standing up like on a lectern, so it reads from any side of the turntable.
      const pg = G(g, 0, 0.4, 0);
      pg.rotation.set(1.15, 0.2, 0.08);
      M(new THREE.BoxGeometry(0.4, 0.012, 0.52), glass, pg);
      // The torn edge: a few teeth of glass along the bottom.
      for (let i = 0; i < 5; i++) {
        const tooth = M(new THREE.BoxGeometry(0.07, 0.012, 0.05), glass, pg);
        tooth.position.set(-0.16 + i * 0.08, 0, 0.27 + (i % 2) * 0.02);
        tooth.rotation.y = (i % 2 ? 1 : -1) * 0.3;
      }
      // The curled corner.
      const cu = M(new THREE.CylinderGeometry(0.035, 0.035, 0.12, 8, 1, true, 0, Math.PI * 1.3), glass, pg);
      cu.position.set(0.19, 0.03, -0.24);
      cu.rotation.z = Math.PI / 2;
      // Lines of burning script.
      for (let l = 0; l < 6; l++) {
        const words = 2 + ((l * 7) % 3);
        let x = -0.15;
        for (let w = 0; w < words && x < 0.15; w++) {
          const len = 0.04 + ((l + w * 3) % 4) * 0.018;
          // Burnt through: the script shows on both faces.
          M(new THREE.BoxGeometry(len, 0.022, 0.014), lit, pg).position.set(x + len / 2, 0, -0.19 + l * 0.07);
          x += len + 0.025;
        }
      }
      const f = flame(pg, V(-0.17, 0.02, 0.22), 0.55, rng, col);
      const gl = glowSprite(g, col, 0.9, V(0, 0.45, 0), 0.32, true);
      return done(c, 0.8, (n) => {
        f.flick();
        gl.material.opacity = 0.26 + (n % 6) * 0.015;
      });
    }
    case 'lucanCenser': {
      // A censer of black glass on its chain, violet light through the holes of its lid, smoke rising from it.
      const ce = G(g, 0, 0.2, 0);
      ce.scale.setScalar(1.3);
      M(new THREE.SphereGeometry(0.15, 12, 6, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), glass, ce).position.y = 0.12;
      M(new THREE.CylinderGeometry(0.05, 0.07, 0.06, 10), glass, ce).position.y = -0.04;
      ring(ce, 0.152, 0.014, iron, 0.12, 5, 20);
      const lid = M(new THREE.SphereGeometry(0.15, 12, 5, 0, Math.PI * 2, 0, Math.PI / 2), glass, ce);
      lid.position.y = 0.13;
      lid.scale.y = 0.8;
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const hole = M(new THREE.SphereGeometry(0.022, 5, 4), lit, ce);
        hole.position.set(Math.cos(a) * 0.115, 0.2, Math.sin(a) * 0.115);
      }
      M(new THREE.SphereGeometry(0.03, 6, 5), iron, ce).position.y = 0.26;
      // Three chains up to a ring.
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        limb(ce, V(Math.cos(a) * 0.14, 0.13, Math.sin(a) * 0.14), V(0, 0.58, 0), 0.006, 0.006, iron, 3);
      }
      ring(ce, 0.04, 0.01, iron, 0.6, 4, 12);
      const inner = glowSprite(ce, col, 0.55, V(0, 0.14, 0), 0.6, true);
      const smoke: THREE.Sprite[] = [];
      for (let i = 0; i < 5; i++) smoke.push(glowSprite(g, '#b8aecb', 0.18, V(0, 0.5, 0), 0.5, true));
      const t = (n: number): void => {
        ce.rotation.z = Math.sin(n * 0.25) * 0.12;
        inner.material.opacity = 0.45 + (n % 5) * 0.04;
        smoke.forEach((sm, i) => {
          const p = (i / 5 + n * 0.025) % 1;
          sm.position.set(Math.sin(p * 5 + i) * 0.06, 0.5 + p * 0.45, Math.cos(p * 4 + i) * 0.05);
          sm.material.opacity = 0.4 * (1 - p);
          sm.scale.setScalar(0.12 + p * 0.2);
        });
      };
      t(0);
      return done(c, 0.95, t);
    }
    case 'bruniHammer': {
      // A forge hammer: a head of black glass split by cyan light, on a short iron-banded haft.
      const hm = G(g, 0, 0.08, 0);
      hm.rotation.set(0, 0.6, -0.35);
      const haft = cached('unRelicHaft', () => std('#1e1712', { roughness: 0.9 }));
      M(new THREE.CylinderGeometry(0.028, 0.032, 0.62, 7), haft, hm).position.y = 0.31;
      for (const y of [0.08, 0.5]) M(new THREE.CylinderGeometry(0.036, 0.036, 0.04, 7), iron, hm).position.y = y;
      const head = G(hm, 0, 0.66, 0);
      M(new THREE.BoxGeometry(0.36, 0.16, 0.16), glass, head);
      for (const k of [-1, 1]) M(new THREE.BoxGeometry(0.03, 0.18, 0.18), iron, head).position.x = k * 0.17;
      crack(head, 0.02, 0.01, 0.082, 0.24, 0.25);
      crack(head, -0.06, -0.03, 0.082, 0.1, -0.8);
      const face = M(new THREE.BoxGeometry(0.012, 0.12, 0.12), lit, head);
      face.position.x = 0.19;
      const gl = glowSprite(head, col, 0.6, V(0.2, 0, 0), 0.45, true);
      return done(c, 0.9, (n) => {
        gl.material.opacity = 0.35 + (n % 6 < 3 ? 0.12 : 0);
      });
    }
    case 'ragnaHorn': {
      // A war horn of black glass bound in crimson-lit bands, its mouth glowing.
      const h = G(g, 0, 0.3, 0);
      h.rotation.y = 0.4;
      const pts: THREE.Vector3[] = [];
      for (let i = 0; i <= 10; i++) {
        const t = i / 10;
        pts.push(V(-0.32 + t * 0.6, Math.sin(t * Math.PI) * 0.18 + t * 0.14, 0));
      }
      for (let i = 0; i < 10; i++) limb(h, pts[i]!, pts[i + 1]!, 0.022 + i * 0.012, 0.022 + (i + 1) * 0.012, glass, 10);
      for (const i of [2, 5, 8]) {
        const b = M(new THREE.TorusGeometry(0.03 + i * 0.012, 0.011, 5, 14), lit, h);
        b.position.copy(pts[i]!);
        b.quaternion.setFromUnitVectors(V(0, 0, 1), new THREE.Vector3().subVectors(pts[i + 1]!, pts[i]!).normalize());
      }
      const mouth = M(new THREE.CircleGeometry(0.13, 14), lit, h);
      mouth.position.copy(pts[10]!);
      mouth.quaternion.setFromUnitVectors(V(0, 0, 1), new THREE.Vector3().subVectors(pts[10]!, pts[9]!).normalize());
      limb(h, pts[1]!, pts[9]!, 0.004, 0.004, iron, 3);
      const gl = glowSprite(h, col, 0.7, pts[10]!.clone(), 0.45, true);
      return done(c, 0.8, (n) => {
        gl.material.opacity = 0.35 + (n % 7) * 0.02;
      });
    }
    case 'eddaShield': {
      // Her shield: black glass, a cold white cross of light, the rim cracked with it.
      const sh = G(g, 0, 0.42, 0);
      sh.rotation.set(-0.1, 0.35, 0.05);
      sh.add(heaterShield(0.44, 0.6, glass));
      const rim = heaterShield(0.48, 0.64, iron);
      rim.position.z = -0.012;
      sh.add(rim);
      // The cross, on the face and burnt through to the back.
      for (const z of [0.05, -0.03]) {
        M(new THREE.BoxGeometry(0.04, 0.42, 0.012), pale, sh).position.set(0, 0.0, z);
        M(new THREE.BoxGeometry(0.28, 0.04, 0.012), pale, sh).position.set(0, 0.1, z);
      }
      crack(sh, 0.12, -0.08, 0.05, 0.12, 0.9, 0.01);
      crack(sh, -0.1, 0.18, 0.05, 0.1, -0.4, 0.01);
      const gl = glowSprite(sh, col, 0.9, V(0, 0.06, 0.1), 0.4, true);
      return done(c, 0.85, (n) => {
        gl.material.opacity = 0.32 + Math.sin(n * 0.3) * 0.06;
      });
    }
    case 'kaelaSeed': {
      // A seed of black glass split by moss light, a sprout of two lit leaves out of the crack.
      const sd = G(g, 0, 0.3, 0);
      const body = M(new THREE.SphereGeometry(0.16, 12, 9), glass, sd);
      body.scale.set(1, 1.35, 0.9);
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.3;
        const ln = M(new THREE.BoxGeometry(0.012, 0.3, 0.012), lit, sd);
        ln.position.set(Math.cos(a) * 0.15, 0, Math.sin(a) * 0.135);
        ln.rotation.set(Math.sin(a) * 0.3, 0, -Math.cos(a) * 0.3);
      }
      limb(sd, V(0, 0.2, 0), V(0.03, 0.36, 0), 0.012, 0.009, lit, 5);
      for (const k of [-1, 1]) {
        const lf = M(new THREE.SphereGeometry(0.05, 7, 5), lit, sd);
        lf.scale.set(1, 0.25, 0.5);
        lf.position.set(0.03 + k * 0.05, 0.36, 0);
        lf.rotation.z = k * 0.5;
      }
      // Roots of black glass under it.
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2;
        limb(sd, V(0, -0.18, 0), V(Math.cos(a) * 0.16, -0.28, Math.sin(a) * 0.16), 0.014, 0.005, glass, 4);
      }
      const gl = glowSprite(sd, col, 0.75, V(0, 0.05, 0), 0.4, true);
      return done(c, 0.8, (n) => {
        sd.rotation.y = n * 0.08;
        gl.material.opacity = 0.32 + (n % 8) * 0.012;
      });
    }
  }
};
